import React, { PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";
import { useForcedRefresh } from "./use-forced-refresh";

interface TooltipBoundaryContext {
    boundary: { current: HTMLDivElement | null };
    updateFlag: Date,
    update: () => void
}

const tooltipBoundaryContext = React.createContext<TooltipBoundaryContext>({
    boundary: { current: null },
    update: () => { throw new Error('No Provider'); },
    updateFlag: new Date()
})

export function TooltipBoundary({ children, ...props }: PropsWithChildren<Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onScroll' | 'ref'>>) {

    const [ref, setRef] = useState<{ current: HTMLDivElement | null }>({ current: null });
    const boundaryCallback = useCallback((ref: HTMLDivElement | null) => {
        setRef({
            current: ref
        })
    }, [])
    const { forcedRefresh, update } = useForcedRefresh();

    const context = useMemo(() => {
        return {
            boundary: ref,
            updateFlag: forcedRefresh,
            update: update
        }
    }, [ref, forcedRefresh, update])

    return (
        <div ref={boundaryCallback} onScroll={update} {...props}>
            <tooltipBoundaryContext.Provider value={context}>
                {children}
            </tooltipBoundaryContext.Provider>
        </div>
    );
}

export function useTooltipBoundary() {
    return useContext(tooltipBoundaryContext);
}

export function useTooltipPlacementUpdate() {
    const { update } = useTooltipBoundary();
    return update;
}
